import { easeExpOut } from "d3-ease";
import * as React from "react";
import { NodeGroup } from "react-move";
import { clamp, updateOrder } from "Utils/OrderUtils";
import styles from "./HorizontalCardGrid.scss";


interface IProps {
    title: string;
    children?: React.ReactNode[];
    data?: object[];
    onUpdate?: (data?: object[]) => void;
    selectedCardKey?: string;
}

interface IState {
    children: React.ReactNode[];
    data?: object[];
    leftDeltaX: number;
    topDeltaY: number;
    mouseX: number;
    mouseY: number;
    isPressed: boolean;
    lastPressed: React.ReactNode;
    itemWidth: number;
    itemHeight: number;
    prevIndex?: number;
    newIndex?: number;
    selectedCardKey?: string;
}

const itemsPerRow = 5;
const MOVE_TIMEOUT = 500;

export class HorizontalCardGrid extends React.Component<IProps, IState> {
    private cardsRef = React.createRef<HTMLDivElement>();
    private mouseDownStartTimer?: number;

    constructor(props: IProps) {
        super(props);

        this.state = {
            children: HorizontalCardGrid.flatMapChildren(props.children),
            data: props.data,
            leftDeltaX: 0,
            topDeltaY: 0,
            mouseX: 0,
            mouseY: 0,
            isPressed: false,
            lastPressed: 0,
            itemWidth: 0,
            itemHeight: 0
        };

        this.updateCardSizes = this.updateCardSizes.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.getLeftUpperCorner = this.getLeftUpperCorner.bind(this);
        this.update = this.update.bind(this);

        window.addEventListener("resize", this.updateCardSizes);
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const state: Partial<IState> = {};
        if (nextProps.children !== prevState.children) {

            const nextChildren = HorizontalCardGrid.flatMapChildren(nextProps.children);
            state.children = prevState.children;
            
            // This is used for adding the children in the correct position
            let addIndex = -1;
            nextChildren.forEach(child => {
                const childIndex = state.children!.findIndex(x => (x as any).key === child.key);

                if (childIndex !== -1) {
                    state.children![childIndex] = child;
                }
                else {
                    if (nextProps.data) {
                        state.children!.splice(nextProps.data.length - 1 + ++addIndex, 0, child);
                    }
                    else {
                        state.children!.push(child);
                    }
                }
            });

            for (let i = 0; i < state.children.length; ++i) {
                const child = state.children[i] as any;
                if (nextChildren.findIndex(x => (x as any).key === child.key) === -1) {
                    state.children.splice(i, 1);
                    i--;
                }
            }
        }

        if (nextProps.data !== prevState.data) {
            state.data = nextProps.data;
        }

        if (nextProps.selectedCardKey !== prevState.selectedCardKey) {
            state.selectedCardKey = nextProps.selectedCardKey;
        }
        return state;
    }

    public componentDidMount = this.updateCardSizes;

    public updateCardSizes() {
        if (this.cardsRef.current) {
            const firstCard = this.cardsRef.current.firstChild as HTMLDivElement;
            if (firstCard) {
                const width = firstCard.clientWidth;
                const height = firstCard.clientHeight;

                if (width !== this.state.itemWidth || height !== this.state.itemHeight) {
                    this.setState({
                        itemWidth: width,
                        itemHeight: height
                    });
                }
            }
        }
    }

    public static flatMapChildren(children?: any[]): any[] {
        const result: any[] = [];
        if (children) {
            if (children.length) {
                children.forEach(child => {
                    if (child) {
                        if (child.length) {
                            // A for-of loop wont work for an React node array, 
                            // disabled the linter warning for now
                            // tslint:disable-next-line: prefer-for-of
                            for (let i = 0; i < child.length; ++i) {
                                child[i] && result.push(child[i]);
                            }
                        }
                        else {
                            result.push(child);
                        }
                    }
                });
            }
            else {
                result.push(children);
            }
        }
        return result;
    }

    public handleTouchStart(key: string, node: React.ReactNode, pressX: number, pressY: number, e: React.TouchEvent<HTMLDivElement>) {
        if(this.state.selectedCardKey !== key) {
            this.mouseDownStartTimer = window.setTimeout(() => this.setState({ isPressed: true }), MOVE_TIMEOUT);

            this.setState({
                leftDeltaX: e.touches[0].pageX - pressX,
                topDeltaY: e.touches[0].pageY - pressY,
                mouseX: pressX,
                mouseY: pressY,
                isPressed: true,
                lastPressed: node,
                itemWidth: e.currentTarget.clientWidth,
                itemHeight: e.currentTarget.clientHeight,
                prevIndex: this.state.children.indexOf(node)
            });

            window.addEventListener("touchmove", this.handleTouchMove);
            window.addEventListener("touchend", this.handleTouchEnd);   
        }     
    }

    public handleTouchMove(e: any) {

        e.preventDefault();
        this.handleMouseMove(e.touches[0]);
    }

    public handleMouseDown(key: string, node: React.ReactNode, pressX: number, pressY: number, e: React.MouseEvent) {
        if(this.state.selectedCardKey !== key) {
            this.mouseDownStartTimer = window.setTimeout(() => this.setState({ isPressed: true }), MOVE_TIMEOUT);

            this.setState({
                leftDeltaX: e.pageX - pressX,
                topDeltaY: e.pageY - pressY,
                mouseX: pressX,
                mouseY: pressY,
                lastPressed: node,
                itemWidth: e.currentTarget.clientWidth,
                itemHeight: e.currentTarget.clientHeight,
                prevIndex: this.state.children.indexOf(node)
            });

            window.addEventListener("mousemove", this.handleMouseMove);
            window.addEventListener("mouseup", this.handleMouseUp);
        }
    }

    public handleMouseMove(e: { pageX: number, pageY: number }) {

        const { leftDeltaX, topDeltaY, isPressed, children, lastPressed, itemWidth, itemHeight } = this.state;

        if (isPressed) {

            const mouseX = e.pageX - leftDeltaX;
            const mouseY = e.pageY - topDeltaY;

            const currentRow = clamp(
                Math.floor(mouseY / itemHeight),
                0,
                Math.ceil(this.state.children.length / 5) - 1
            );
            const currentColumn = clamp(
                Math.floor(mouseX / itemWidth),
                0,
                itemsPerRow
            );

            let newOrder = children;
            const newIndex = clamp(
                Math.floor(currentRow * itemsPerRow) + currentColumn,
                0,
                (this.state.data ? this.state.data.length : this.state.children.length) - 1
            );
            const curIndex = children.indexOf(lastPressed);
            if (curIndex >= 0 && newIndex !== curIndex) {
                newOrder = updateOrder(children, curIndex, newIndex);
            }

            this.setState({ mouseX, mouseY, children: newOrder, newIndex });
        }
    }

    private update() {
        if (this.state.data
            && this.props.onUpdate
            && this.state.newIndex !== undefined
            && this.state.prevIndex !== undefined
            && this.state.prevIndex >= 0
            && this.state.newIndex !== this.state.prevIndex) {
            const data = updateOrder(this.state.data, this.state.prevIndex, this.state.newIndex);
            this.setState({ data, newIndex: undefined, isPressed: false });
            this.props.onUpdate(data);
            return;
        }

        this.setState({ newIndex: undefined, isPressed: false });
    }

    public handleMouseUp() {
        this.update();

        window.removeEventListener("mouseup", this.handleMouseUp);
        window.removeEventListener("mousemove", this.handleMouseMove);
        window.clearTimeout(this.mouseDownStartTimer);
    }

    public handleTouchEnd() {
        this.update();

        window.removeEventListener("touchmove", this.handleTouchMove);
        window.removeEventListener("touchend", this.handleTouchEnd);
        window.clearTimeout(this.mouseDownStartTimer);
    }

    public getLeftUpperCorner(index: number): { x: number, y: number } {
        return {
            x: index % itemsPerRow * this.state.itemWidth,
            y: Math.floor(index / itemsPerRow) * this.state.itemHeight
        };
    }

    private range(length: number): number[] {
        return Array.from({ length }, (_, i) => i);
    }

    public render() {
        const { mouseX, mouseY, isPressed, lastPressed, children, itemHeight, selectedCardKey } = this.state;
        const getLeftUpperCorner = this.getLeftUpperCorner;
        const cardsRef = this.cardsRef;

        return (
            <div className={styles.horizontalCardGrid}>
                <h1>{this.props.title}</h1>
                <NodeGroup
                    data={this.range(children.length)}
                    keyAccessor={d => `${(children[d] as any).key}`}
                    start={d => {
                        const cardPos = getLeftUpperCorner(d);

                        return {
                            scale: 1,
                            shadow: 1,
                            x: cardPos.x,
                            y: cardPos.y,
                        };
                    }}
                    update={d => {
                        const dragging = lastPressed === children[d] && isPressed;
                        const cardPos = getLeftUpperCorner(d);

                        return {
                            scale: [dragging ? 1.1 : 1],
                            x: [cardPos.x],
                            y: [cardPos.y],
                            timing: { duration: 350, ease: easeExpOut }
                        };
                    }}
                >
                    {nodes => (
                        <div className={styles.cardsWrapper} ref={cardsRef} style={{ height: itemHeight * Math.ceil(children.length / itemsPerRow) }}>
                            {nodes.map(({ key, data, state }, i) => {
                                
                                const item = children[data] as any;

                                if (!item) {
                                    return;
                                }

                                const { scale, x, y } = state;

                                const transX = lastPressed === item && isPressed ? mouseX : x;
                                const transY = lastPressed === item && isPressed ? mouseY : y;

                                const l = {
                                    ...item,
                                    key,
                                    props: {
                                        ...item.props,
                                        className: lastPressed === item && isPressed && styles.dragging,
                                        onMouseDown: (e: any) => this.handleMouseDown(key, item, x, y, e),
                                        onTouchStart: (e: any) => this.handleTouchStart(key, item, x, y, e),
                                        style: {
                                            transform: `translate3d(${transX}px, ${transY}px, 0) scale(${scale})`,
                                            WebkitTransform: `translate3d(${transX}px, ${transY}px, 0) scale(${scale})`,
                                            zIndex: selectedCardKey === key ? 99997 : item === lastPressed ? 99 : data
                                        }
                                    }
                                };

                                return l;
                            })}
                        </div>
                    )}
                </NodeGroup>
            </div>
        );
    }
}

export default HorizontalCardGrid;


// WEBPACK FOOTER //
// ./src/Components/Common/HorizontalCardGrid/HorizontalCardGrid.tsx